import { ChangeDetectionStrategy, Component, inject, signal, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { RRule } from 'rrule';
import { defaultRruleFromWindow } from '@zag/common/validity';
import { DateRangePicker, type DateRangeValue } from '../date-range-picker/date-range-picker';
import { RruleEditor } from '../rrule-editor/rrule-editor.component';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';
import { ZagSwitch } from '../switch/switch.component';

export interface ValidityValue {
  start: string | null;
  end: string | null;
  rrule: string | null;
}

export interface ValidityDialogData extends ValidityValue {
  extraContent?: TemplateRef<unknown>;
}
export type ValidityDialogResult = ValidityValue | null;

const DEFAULT_WEEKLY_RRULE = 'FREQ=WEEKLY;BYDAY=MO';

@Component({
  selector: 'zag-validity-dialog',
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, DateRangePicker, RruleEditor, ZagButton, ZagIcon, ZagSwitch],
  templateUrl: './validity-dialog.component.html',
  styleUrl: './validity-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class ValidityDialog {
  protected readonly data = inject<ValidityDialogData>(DIALOG_DATA);
  private readonly dialogRef = inject<DialogRef<ValidityDialogResult>>(DialogRef);

  protected readonly rangeControl = new FormControl<DateRangeValue>(
    {
      start: this.data.start,
      end: this.data.end,
    },
    { nonNullable: true }
  );
  protected readonly range = signal<DateRangeValue>(this.rangeControl.getRawValue());
  protected readonly rrule = signal(initialRrule(this.data));
  protected readonly recurrenceEnabled = signal(true);
  private syntheticPrefill = syntheticPrefill(this.data, this.rrule());
  private readonly initialRruleValue = this.rrule();
  private suppressInitialEditorNormalization = true;

  constructor() {
    this.rangeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      this.range.set(value);
      this.updateSyntheticPrefill(value);
    });
  }

  protected onRruleChange(value: string): void {
    if (this.suppressInitialEditorNormalization) {
      this.suppressInitialEditorNormalization = false;
      if (equivalentInitialRule(value, this.initialRruleValue)) {
        this.rrule.set(this.initialRruleValue);
        return;
      }
    }
    if (this.data.rrule === null && equivalentInitialRule(value, this.syntheticPrefill)) {
      this.rrule.set(this.syntheticPrefill);
      return;
    }
    this.rrule.set(value);
  }

  protected onRecurrenceToggle(enabled: boolean): void {
    this.recurrenceEnabled.set(enabled);
  }

  protected onApply(): void {
    const range = this.rangeControl.getRawValue();
    const currentRrule = this.rrule().trim();
    const rrule = this.recurrenceEnabled() && currentRrule && currentRrule !== this.syntheticPrefill ? currentRrule : null;
    this.dialogRef.close({ ...range, rrule });
  }

  protected onCancel(): void {
    this.dialogRef.close(null);
  }

  protected onEscapeKey(): void {
    this.onCancel();
  }

  private updateSyntheticPrefill(range: DateRangeValue): void {
    if (this.data.rrule !== null || this.rrule() !== this.syntheticPrefill) return;

    const next = initialRrule({ ...range, rrule: null });
    if (next === this.syntheticPrefill) return;
    this.syntheticPrefill = next;
    this.rrule.set(next);
  }
}

function initialRrule(data: ValidityDialogData): string {
  const existing = data.rrule?.trim();
  if (existing) return existing;
  if (data.start !== null && data.end !== null) return defaultRruleFromWindow(data.start, data.end);
  return DEFAULT_WEEKLY_RRULE;
}

function syntheticPrefill(data: ValidityDialogData, initial: string): string {
  return data.rrule === null ? initial : '';
}

function equivalentInitialRule(value: string, initial: string): boolean {
  if (value === initial) return true;
  const valueOpts = parseRruleOptions(value);
  const initialOpts = parseRruleOptions(initial);
  if (!valueOpts || !initialOpts) return false;
  return JSON.stringify(canonicalRruleShape(valueOpts)) === JSON.stringify(canonicalRruleShape(initialOpts));
}

function parseRruleOptions(rrule: string): Record<string, unknown> | null {
  try {
    return RRule.fromString(`RRULE:${rrule}`).origOptions as Record<string, unknown>;
  } catch {
    return null;
  }
}

function canonicalRruleShape(opts: Record<string, unknown>): Record<string, unknown> {
  const { until, ...rest } = opts;
  return { ...rest, until: until instanceof Date ? until.toISOString().slice(0, 10) : until };
}

export function isValidityValue(value: unknown): value is ValidityValue {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { start?: unknown; end?: unknown; rrule?: unknown };
  return (
    (candidate.start === null || typeof candidate.start === 'string') &&
    (candidate.end === null || typeof candidate.end === 'string') &&
    (candidate.rrule === null || typeof candidate.rrule === 'string')
  );
}
